import { User } from ".";

export type MessageType =
  | "text"
  | "image"
  | "video"
  | "file"
  | "audio"
  | "button"
  | "calendar-week"
  | "customize-bot"
  | "placeholder"
  | "charts"
  | "flash_card"
  | "image_collection"
  | "flash_card_image_collection"
  | "input"
  | "crash_course_form"
  | "flipping_card"
  | "tabbed_message"
  | "visualization_message";

export const MESSAGE_TYPES_BLOCKED_FOR_TTS: ReadonlySet<MessageType> = new Set([
  "image",
  "audio",
  "video",
  "file",
  "charts",
  "image_collection",
  "flash_card_image_collection",
  "visualization_message",
]);

export type MessegeButtons = {
  text: string;
  data: string;
  isCorrect?: boolean;
  isChosen?: boolean;
};

export type MessageState = "default" | "disable";

export type DayWithHours = {
  day: string;
  hours: number[];
};

export type MessagePrev = {
  date: string;
  message_id: string;
  text: string;
  title?: string;
  file_url?: string;
  domain: string;
  focus: string;
  type: MessageType;
  tabbed_content?: TabbedMessageContent[];
  score?: number;
};

type MessageChat = {
  id: string;
  type: "private" | "group" | "channel";
};

// Payload from POST /message_menu
export type MessageMenuResponse = {
  version?: number;
  items: MessageMenuItem[];
};

export type MessageMenuItem = {
  id: string;
  kind: "action" | "submenu";
  label: string;
  icon?: string;
  disabled?: boolean;
  meta?: MessageMenuItemMeta;
  children?: MessageMenuItem[];
};

export type MessageMenuItemMeta = {
  action?: string;
  tool_key?: string;
  description?: string;
  template?: unknown;
  name?: string;
};

// Hint for POST /message_menu (preset + exclusions); not a rendered menu tree
export type MessageMenuHint = {
  preset: string;
  exclude_ids: string[];
};

export type MessageVisualizationImage = {
  url: string;
  mime_type?: string;
};

export type MessageVisualizationUi = {
  title?: string;
  card_variant?: string;
};

export type MessageVisualization = {
  source_message_id?: string;
  tool_key?: string;
  image?: MessageVisualizationImage;
  images?: MessageVisualizationImage[];
  actions?: {
    copy_image?: boolean;
    download_image?: boolean;
  };
  ui?: MessageVisualizationUi;
};

export function isMessageMenuHint(
  menu: MessageMenuItem[] | MessageMenuHint | undefined,
): menu is MessageMenuHint {
  return (
    !!menu &&
    typeof menu === "object" &&
    !Array.isArray(menu) &&
    typeof (menu as MessageMenuHint).preset === "string" &&
    Array.isArray((menu as MessageMenuHint).exclude_ids)
  );
}

export type SystemMessage = {
  is_browser_locked: boolean;
};

export type SystemAudio = {
  play_sound_answer_is_correct: boolean | null;
};

export type SystemBadgeAudio = {
  play_sound_badge: boolean | null;
};

export type SystemShowLessonModal = {
  show_lesson_onboarding: boolean;
};
export type SystemScrollToMessage = {
  domain: string;
  focus: string;
  scroll_to_message_id: string;
};

export type SystemStartRecording = {
  start_recording: boolean;
  domain: string;
  focus: string;
};

export type FlashCard = {
  title: string;
  text: string;
  subtitle: string;
  custom_background: boolean | null;
  title_step: string;
};

export type FlippingCardItem = {
  title?: string;
  front_text: string;
  back_text: string;
};

export type ImageColetion = {
  url: string;
  description: string;
};

export type TabStatus = "pending" | "streaming" | "completed";
export type QuizTabStatus = "new" | "correct" | "incorrect";

// Timed transcript for audio sync. When `words` is set, the UI can highlight
// tokens inside the line (“karaoke”) using current playback time in seconds.
export type TranscriptionWordTimed = {
  w: string;
  start_sec: number;
  end_sec: number;
};

export type TranscriptionSegmentTimed = {
  id?: string;
  speaker?: string;
  start_sec: number;
  end_sec: number;
  text: string;
  words?: TranscriptionWordTimed[];
};

export type TranscriptionTimedPayload = {
  format: "segments_v1";
  language?: string;
  dialogue_start_sec?: number;
  offset_sec?: number;
  start_offset_sec?: number;
  leading_silence_sec?: number;
  segments: TranscriptionSegmentTimed[];
};

export type AudioTranscriptionDialogProps = {
  open: boolean;
  onClose: () => void;
  markdown?: string;
  timed?: TranscriptionTimedPayload;
  currentTimeSec?: number;
  onSeekToSecond?: (sec: number) => void;
  registerTranscriptPlayerHost?: (el: HTMLDivElement | null) => void;
  audioOverview?: {
    audioUrl: string;
    isPlaying: boolean;
    currentTimeSec: number;
    durationSec: number;
    title?: string;
    doctorName?: string;
    onPlayPause?: () => void;
    playbackRate?: number;
    onPlaybackRateChange?: (rate: number) => void;
  };
};

// Content block types for tabbed messages
export type TextContentBlock = {
  type: "text";
  content: string;
};

export type ImageContentBlock = {
  type: "image";
  file_url: string;
  alt?: string;
};

export type ImageCollectionContentBlock = {
  type: "image_collection";
  image_collection: ImageColetion[];
};

export type FlashCardContentBlock = {
  type: "flash_card";
  flash_card: FlashCard[][];
};

export type FlashCardImageCollectionContentBlock = {
  type: "flash_card_image_collection";
  image_collection: ImageColetion[];
};

export type AudioContentBlock = {
  type: "audio";
  audio_url: string;
  title?: string;
  transcription?: string;
  transcription_timed?: TranscriptionTimedPayload;
};

export type VideoContentBlock = {
  type: "video";
  video_url: string;
  title?: string;
};

export type PdfContentBlock = {
  type: "pdf";
  pdf_url: string;
  title?: string;
};

export type FlippingCardContentBlock = {
  type: "flipping_card";
  flipping_card: FlippingCardItem[];
};

export type QuizContentBlock = {
  type: "quiz";
  text?: string;
  buttons?: MessegeButtons[][];
  state?: MessageState;
};

export type ButtonsContentBlock = {
  type: "buttons";
  text?: string;
  buttons?: MessegeButtons[][];
  state?: MessageState;
};

export type ChartContentBlock = {
  type: "chart";
  chart_name: string;
  items: Array<{ name: string; value: number }>;
};

export type FeedbackContentBlock = {
  type: "feedback";
  text?: string;
  title?: string;
  score?: number;
};

export type InteractiveOption = {
  id: string;
  text: string;
};

export type InteractiveBlank = {
  id: string;
  label?: string;
};

export type FillInBlankContentBlock = {
  type: "fill_in_blank";
  data?: string;
  title?: string;
  instruction?: string;
  template: string;
  blanks: InteractiveBlank[];
  options: InteractiveOption[];
  state?: MessageState;
  is_correct?: boolean;
  answers?: Record<string, string>;
  user_answers?: Record<string, string>;
  blank_results?: Record<string, { correct: boolean }>;
  correct_answers?: Record<string, string>;
  feedback?: string;
};

export type OrderingContentBlock = {
  type: "ordering";
  data?: string;
  title?: string;
  instruction?: string;
  prompt?: string;
  slot_count: number;
  options: InteractiveOption[];
  state?: MessageState;
  is_correct?: boolean;
  answers?: string[];
  user_order?: string[];
  slot_results?: Array<{ option_id: string; correct: boolean }>;
  correct_order?: string[];
  feedback?: string;
};

export type MatchingPairResult = {
  left_id: string;
  right_id: string;
  correct?: boolean;
};

export type MatchingContentBlock = {
  type: "matching";
  data?: string;
  title?: string;
  instruction?: string;
  left: InteractiveOption[];
  right: InteractiveOption[];
  state?: MessageState;
  is_correct?: boolean;
  answers?: Record<string, string>;
  user_pairs?: MatchingPairResult[];
  correct_pairs?: MatchingPairResult[];
  feedback?: string;
};

export type OrderedResponseContentBlock = {
  type: "ordered_response";
  data?: string;
  title?: string;
  instruction?: string;
  prompt?: string;
  slot_count: number;
  options: InteractiveOption[];
  state?: MessageState;
  is_correct?: boolean;
  answers?: string[];
  user_order?: string[];
  correct_order?: string[];
  feedback?: string;
};

export type ContentBlock =
  | TextContentBlock
  | ImageContentBlock
  | ImageCollectionContentBlock
  | FlashCardContentBlock
  | FlashCardImageCollectionContentBlock
  | AudioContentBlock
  | VideoContentBlock
  | PdfContentBlock
  | FlippingCardContentBlock
  | QuizContentBlock
  | ButtonsContentBlock
  | ChartContentBlock
  | FeedbackContentBlock
  | FillInBlankContentBlock
  | OrderingContentBlock
  | MatchingContentBlock
  | OrderedResponseContentBlock;

export const CONTENT_BLOCK_TYPES_BLOCKED_FOR_TTS: ReadonlySet<
  ContentBlock["type"]
> = new Set([
  "image",
  "audio",
  "video",
  "pdf",
  "chart",
  "buttons",
  "fill_in_blank",
  "ordering",
  "matching",
  "ordered_response",
  "image_collection",
  "flash_card_image_collection",
]);

// Tab ids that must not use TTS — server `/message_to_audio` still speaks tab title without client text.
export const TAB_IDS_BLOCKED_FOR_TTS: ReadonlySet<string> = new Set([
  "cover_image",
]);

export type TabbedMessageContent = {
  tab_id: string;
  tab_label: string;
  // Legacy: plain markdown (used when content_blocks is absent)
  content?: string;
  content_blocks?: ContentBlock[];
  quiz_status?: QuizTabStatus;
  transcription?: string;
  transcription_timed?: TranscriptionTimedPayload;
  csu_app_forward?: boolean;
};

export type MessageTypeColor =
  | "system"
  | "default"
  | "chips"
  | "search_result_low"
  | "search_result_middle"
  | "search_result_high"
  | "disable"
  | "command"
  | "suggestion"
  | "feedback"
  | "info"
  | "stars0"
  | "stars5";

export interface Message extends MessagePrev {
  description?: string | undefined;
  is_bot: boolean;
  chat?: MessageChat;
  from_user?: User;
  title?: string;
  file_url?: string;
  buttons?: MessegeButtons[][];
  user_id: string;
  isMultiple?: boolean;
  days?: DayWithHours[];
  availableDays?: number;
  availableHoursPerDay?: number;
  collapse?: boolean;
  reaction?: string;
  menu?: MessageMenuItem[] | MessageMenuHint;
  isSaved?: boolean;
  tag?: string[];
  state?: MessageState;
  message_type: MessageTypeColor;
  linked_with_previous?: boolean;
  refreshedLeaderBoard?: boolean;
  isPlaceholder?: boolean;
  minHeightFull?: boolean;
  isStreaming?: boolean;
  isForStreaming?: boolean;
  reply?: string;
  reply_message_text?: string;
  reply_message_id?: string;
  // When jumping to reply, open this tab on a tabbed source message
  reply_message_tab_id?: string | null;
  visualization?: MessageVisualization;
  chart_name?: string;
  items?: Array<{ name: string; value: number }>;
  flash_card?: FlashCard[] | FlashCard[][];
  flipping_card?: FlippingCardItem[];
  bottom_left_arrow?: boolean;
  bottom_right_arrow?: boolean;
  image_collection?: ImageColetion[];
  tabbed_content?: TabbedMessageContent[];
  is_text_input?: boolean;
  stars_count?: number;
  selected_stars?: number;
  isLoading?: boolean;
  score?: number;
  play_gif_once?: boolean;
  transcription?: string;
  transcription_timed?: TranscriptionTimedPayload;
  audio?: {
    audio_url?: string;
    button_text?: string;
    transcription?: string;
    transcription_timed?: TranscriptionTimedPayload;
  };
  domain: string;
  focus: string;
  // true = keep chat history (Continue). false = start a new dialog. Default true.
  use_history?: boolean;
  mentionToken?: string;
  mentionTokens?: string[];
}

export type AudioMessage = Omit<Message, "type"> & { type: "audio" };
